import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Checkbox,
  TextInput,
  MultiSelect,
  MultiSelectOption,
} from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy } from 'corso-types';
import { FormEventHandler } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useDiscountDefinitionCreate } from '~/features/settings/hooks/useDiscountDefinitions';
import { useProductCollections } from '~/features/settings/hooks/useProductCollections';
import { useCurrency } from '~/hooks/useCurrency';
import { CombinationsFieldset } from './CombinationsFieldset';
import { DiscountValueField } from './DiscountValueField';
import { MinimumRequirementFieldset } from './MinimumRequirementFieldset';

const createSchema = z.object({
  title: z.string(),
  discountDefinition: z.object({
    mutation: z.literal('discountCodeBasicCreate'),
    parameters: CrewMerchantUi.amountOffProductRawSchema,
  }),
});
type FormValues = z.infer<typeof createSchema>;

const defaults: FormValues = {
  title: 'Amount off Product Discount',
  discountDefinition: {
    mutation: 'discountCodeBasicCreate',
    parameters: {
      kind: 'amountOffProduct',
      combinesWith: {
        orderDiscounts: false,
        productDiscounts: false,
        shippingDiscounts: false,
      },
      customerGets: {
        items: { collections: { add: [] } },
        value: { percentage: 10 },
      },
    },
  },
};

export function AmountOffProductForm({ onClose }: { onClose: () => void }) {
  const {
    control,
    formState: { errors, isSubmitted },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: defaults,
  });

  const hasErrors = Object.keys(errors).length > 0;

  const { mutate: createDiscountDefinition } = useDiscountDefinitionCreate();
  const { currencyCode, currencySymbol } = useCurrency();
  const { data: productCollections = [] } = useProductCollections();

  // Watch for the "once per order" checkbox visibility — only relevant when
  // the fixed-amount variant is selected.
  const currentValue = useWatch({
    control,
    name: 'discountDefinition.parameters.customerGets.value',
  });
  const isFixedAmount = 'amount' in currentValue;

  const collectionOptions: MultiSelectOption[] = productCollections.map(
    (collection) => ({
      value: `${collection.id}`,
      label: collection.name,
      keywords: [collection.name],
    }),
  );

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => {
      createDiscountDefinition(values);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={submitHandler}>
      {isSubmitted && hasErrors && (
        <Alert variant="warning" heading="Invalid Fields">
          Fix the highlighted fields before saving.
        </Alert>
      )}
      <TextInput
        id="title"
        label="Title"
        details="The name of the discount in Corso."
        required
        {...register('title')}
        error={errors.title?.message}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.customerGets.value"
        render={({ field, fieldState }) => (
          <DiscountValueField
            value={field.value}
            onChange={field.onChange}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.customerGets.items"
        render={({ field }) => (
          <MultiSelect
            label="Applies to"
            details="Customers only get the discount on items in these collections."
            options={collectionOptions}
            placeholder="Select collections"
            value={field.value.collections.add
              .map((id) =>
                collectionOptions.find((option) => option.value === `${id}`),
              )
              .filter(isTruthy)}
            onChange={(selected) =>
              field.onChange({
                collections: {
                  add: selected.map(({ value }) => Number(value)),
                },
              })
            }
            // Zod's `.min(1)` on `collections.add` reports at the deep path;
            // surface it explicitly so empty-collection submits stop being
            // silent.
            error={
              errors.discountDefinition?.parameters?.customerGets?.items
                ?.collections?.add?.message ??
              errors.discountDefinition?.parameters?.customerGets?.items
                ?.collections?.add?.root?.message
            }
          />
        )}
      />

      {isFixedAmount && (
        <Controller
          control={control}
          name="discountDefinition.parameters.customerGets.value"
          render={({ field }) => {
            const appliesOnEachItem =
              'amount' in field.value ? field.value.appliesOnEachItem : false;
            return (
              <div className="flex items-start gap-3 text-sm text-neutral-800">
                <Checkbox
                  id="applies-once-per-order"
                  checked={!appliesOnEachItem}
                  onChange={(e) => {
                    if ('amount' in field.value) {
                      field.onChange({
                        ...field.value,
                        appliesOnEachItem: !e.target.checked,
                      });
                    }
                  }}
                />
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="applies-once-per-order"
                    className="cursor-pointer"
                  >
                    Only apply discount once per order
                  </label>
                  <p className="text-xs text-neutral-500">
                    If not selected, the amount will be taken off each eligible
                    item in an order.
                  </p>
                </div>
              </div>
            );
          }}
        />
      )}

      <Controller
        control={control}
        name="discountDefinition.parameters.minimumRequirement"
        render={({ field }) => (
          <MinimumRequirementFieldset
            value={field.value}
            onChange={field.onChange}
            currencySymbol={currencySymbol}
          />
        )}
      />

      <Controller
        control={control}
        name="discountDefinition.parameters.combinesWith"
        render={({ field }) => (
          <CombinationsFieldset value={field.value} onChange={field.onChange} />
        )}
      />

      <Action variant="primary" type="submit">
        Create
      </Action>
    </form>
  );
}
